import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';

import { CompanySerivces } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import { DisclosureStatementCompanyBuyerOptions } from '@constants_iqloud';

import {
  CreateEditCompanyUserType,
  CreateCompanyType,
  ProfileItemsType,
  UpdateCompanyInformationType,
  CompanyDetailType,
} from '@type-def_iqfu/CompanyType';
import { SupportCitiesData } from '@type-def_iqfu/public/SupportCitiesData';

import { UserType } from '@constants_common';
import { CompanyStep, SearchCompanySortQueries, COMPANIES_SORT_TYPE_FOR_TOP_TITLES } from '@constants_iqfu';

const queryString = require('query-string');

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

export type CompanyTopTitles = {
  company_top_titles: {
    sort_type: keyof typeof COMPANIES_SORT_TYPE_FOR_TOP_TITLES;
    content: string;
  }[];
};

type statusType = 'success' | 'error';

export type CompaniesListItemByAdmin = {
  id: number;
  name: string;
  full_address: string; // 都道府県、市区町村も結合した住所を返す。
  is_public: boolean; // 公開ステータス。
  step: 'created' | 'preparing' | 'inputting' | 'completed'; // ステップ。
  unapproved: boolean; // 未承認紹介の有無。
  iqfu: {
    has_iqfu: boolean;
    start_date: string; // format: date, example: '2021-10-20', イクラ不動産利用開始日。
  };
  cloud: {
    has_cloud: boolean;
    start_date: string; // format: date, example: '2021-10-20', イクラウド利用開始日。
  };
  company_type: 'general_agency' | 'purchase_agency'; // companyのtype。
  company_services: ('iqfu' | 'cloud' | 'pro' | 'leaseback' | 'purchase_agency_search')[]; // 不動産会社の加盟サービス。
};

export type CompaniesListItemByCompany = {
  id: number;
  name: string;
  full_address: string; // 都道府県、市区町村も結合した住所を返す。
  is_public: boolean; // 公開ステータス。
  unapproved: boolean; // 未承認紹介の有無。
  rejected_profile: boolean; // 差し戻し紹介文の有無。
  rejected_profile_image: boolean; // 差し戻し写真の有無。
};

export const useCompanyData = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  type getCompaniesListByAdminApiResponse = {
    offset: number; // 取得データのoffset
    limit: number; // 取得データの件数
    total_count: number; // 取得条件のデータ総数
    companies: CompaniesListItemByAdmin[];
  };

  const getCompaniesListByAdmin = async (queryParams: {
    name?: string;
    address?: string | number | null;
    step?: '' | keyof typeof CompanyStep;
    is_public?: '' | 'true' | 'false';
    unapproved?: boolean;
    rejected?: boolean;
    offset: number | undefined;
    limit: number;
  }): Promise<getCompaniesListByAdminApiResponse> => {
    if (queryParams.offset === undefined || queryParams.offset < 0) queryParams.offset = 0;
    const query = queryString.stringify(queryParams);
    let url = `${backendBasePath}/admin/companies?${query}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type getCompaniesListByCompanyApiResponse = {
    offset: number; // 取得データのoffset
    limit: number; // 取得データの件数
    total_count: number; // 取得条件のデータ総数
    companies: CompaniesListItemByCompany[];
  };

  const getCompaniesListByCompany = async (queryParams: {
    name?: string;
    is_public?: '' | 'true' | 'false';
    rejected?: boolean;
    offset: number | undefined;
    limit: number;
  }): Promise<getCompaniesListByCompanyApiResponse> => {
    if (queryParams.offset === undefined || queryParams.offset < 0) queryParams.offset = 0;
    const query = queryString.stringify(queryParams);
    let url = `${backendBasePath}/company/companies?${query}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type getCompaniesListQuery = {
    prefecture_code?: number;
    city_code?: number;
    sort?: keyof typeof SearchCompanySortQueries;
    limit?: number;
    offset?: number;
  };

  const getPublicCompaniesList = async (queryParams: getCompaniesListQuery) => {
    if (queryParams.prefecture_code === 0) delete queryParams.prefecture_code;
    if (queryParams.city_code === 0) delete queryParams.city_code;
    const query = queryString.stringify(queryParams);

    const url = `${backendBasePath}/companies?${query}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  /**
   * 会社の基本情報を取得する(住所や電話番号など)
   * @param company_id 会社のid
   * @returns
   */
  const getCompanyDetail = async (company_id: string) => {
    const url = `${backendBasePath}/admin/companies/${company_id}`;
    let status;
    const data = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, data };
  };

  const updateCompanyInformationByAdmin = async (company_id: string, submitData: UpdateCompanyInformationType) => {
    const url = `${backendBasePath}/admin/companies/${company_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  /**
   * 会社の概要情報を取得する(営業時間や駐車場の有無など)
   * @param userType roleが何か（admin or company or seller)
   * @param company_id 会社のid
   * @returns
   */
  const getCompanyOverView = async (userType: UserType, company_id: string) => {
    const url = `${backendBasePath}/${userType}/companies/${company_id}/company_overview_items`;
    let status;
    const data = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, data };
  };

  const updateCompanyOverView = async (
    userType: UserType,
    company_id: string,
    submitData: {
      overview_items: {
        id: number;
        content: string;
      }[];
    },
  ) => {
    const url = `${backendBasePath}/${userType}/companies/${company_id}/company_overview_items`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  /**
   * 会社の紹介情報を取得する(アピールポイントや紹介文など)
   * @param userType roleが何か（admin or company or seller)
   * @param company_id 会社のid
   * @returns
   */
  const getCompanyProfileData = async (userType: UserType, company_id: string) => {
    const url = `${backendBasePath}/${userType}/companies/${company_id}/company_profile`;
    let status;
    const data = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, data };
  };

  const getRejectedCompanyProfileData = async (company_id: string) => {
    const url = `${backendBasePath}/company/companies/${company_id}/rejected_company_profile_update_request`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyLastApprovedProfileData = async (company_id: string) => {
    const url = `${backendBasePath}/admin/companies/${company_id}/approved_company_profile_update_request`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateCompanyProfileRequest = async (
    company_id: string,
    submitData: {
      status: 'rejected' | 'approved';
      rejected_reason?: string;
    },
  ) => {
    const url = `${backendBasePath}/admin/companies/${company_id}/company_profile_update_request`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createCompany = async (submitData: CreateCompanyType) => {
    const url = `${backendBasePath}/admin/companies`;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteCompany = async (company_id: string) => {
    const url = `${backendBasePath}/admin/companies/${company_id}`;
    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateStep = async (company_id: string, step: keyof typeof CompanyStep) => {
    const url = `${backendBasePath}/admin/companies/${company_id}/company_registration`;
    let nextStep;
    if (step === 'created') {
      nextStep = 'preparing';
    } else {
      nextStep = 'inputting';
    }
    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ step: nextStep }),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateCompanyDetail = async (company_id: string, submitData: UpdateCompanyInformationType) => {
    const url = `${backendBasePath}/admin/companies/${company_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyUserData = async (company_id: string, offset: number, limit: number) => {
    if (offset < 0) offset = 0;
    const url = `${backendBasePath}/admin/companies/${company_id}/company_users?offset=${offset}&limit=${limit}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createCompanyUser = async (company_id: string, submitData: CreateEditCompanyUserType) => {
    const url = `${backendBasePath}/admin/companies/${company_id}/company_users`;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    console.log('result', result);
    return result;
  };

  const getCompanyUserDetail = async (company_user_id: string) => {
    const url = `${backendBasePath}/admin/company_users/${company_user_id}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyUsersAddress = async (company_id: string) => {
    const url = `${backendBasePath}/company/companies/${company_id}/company_users`;
    let statusAddress;
    const resultAddress = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        statusAddress = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        statusAddress = 'error';
      });
    return { statusAddress, resultAddress };
  };

  const editCompanyUser = async (company_user_id: string, submitData: CreateEditCompanyUserType) => {
    const url = `${backendBasePath}/admin/company_users/${company_user_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteCompanyUser = async (company_user_id: string) => {
    const url = `${backendBasePath}/admin/company_users/${company_user_id}`;
    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyNotificationEmails = async (userType: UserType, company_id: string) => {
    const url = `${backendBasePath}/${userType}/companies/${company_id}/company_notification_emails`;
    let status;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, result };
  };

  const createCompanyNotificationEmails = async (userType: UserType, company_id: string, email: string) => {
    const url = `${backendBasePath}/${userType}/companies/${company_id}/company_notification_emails`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteCompanyNotificationEmails = async (userType: UserType, company_notification_email_id: string) => {
    const url = `${backendBasePath}/${userType}/company_notification_emails/${company_notification_email_id}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  //@hooks_iqloud/useCompanyDataと重複
  const getCompanyOptions = async (query?: string) => {
    const url = `${backendBasePath}/admin/companies/options?q=${query}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });

    return result;
  };

  const getCompanyData = async (
    company_id: string,
  ): Promise<{ status: 'success' | 'error'; data: CompanyDetailType }> => {
    const url = `${backendBasePath}/company/companies/${company_id}/company_detail`;
    let status: 'success' | 'error' = 'error';
    const data = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, data };
  };

  const updateCompanyInformation = async (company_id: string, submitData: UpdateCompanyInformationType) => {
    const url = `${backendBasePath}/company/companies/${company_id}/company_detail`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateCompanyTags = async (company_id: string, submitData: UpdateCompanyInformationType) => {
    const url = `${backendBasePath}/company/companies/${company_id}/company_detail`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateCompanyProfile = async (company_id: string, submitData: ProfileItemsType) => {
    const url = `${backendBasePath}/admin/companies/${company_id}/company_profile`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateCompanyProfileRequests = async (company_id: string, submitData: ProfileItemsType) => {
    const url = `${backendBasePath}/company/companies/${company_id}/company_profile_update_requests`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createProfileImages = async (userType: UserType, company_id: string, formData: FormData) => {
    const url = `${backendBasePath}/${userType}/companies/${company_id}/company_profile_images`;

    for (let pair of formData.entries()) {
      console.log(pair[0] + ' : ' + pair[1]);
    }
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const selectMainProfileImage = async (userType: UserType, company_profile_image_id: number) => {
    const url = `${backendBasePath}/${userType}/company_profile_images/${company_profile_image_id}}/set_main`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteProfileImage = async (userType: UserType, company_profile_image_id: number) => {
    const url = `${backendBasePath}/${userType}/company_profile_images/${company_profile_image_id}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getUnapprovedProfileImages = async () => {
    const url = `${backendBasePath}/admin/unapproved_company_profile_images`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const classifyProfileImage = async (
    company_profile_image_id: number,
    submitData: {
      status: string;
      rejected_reason: string;
    },
  ) => {
    const url = `${backendBasePath}/admin/company_profile_images/${company_profile_image_id}}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyTags = async (userType: UserType) => {
    const url = `${backendBasePath}/${userType}/company_tags`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const activateCompanyService = async ({
    companyId,
    serviceCode,
    activateDate,
  }: {
    companyId: string;
    serviceCode: CompanySerivces;
    activateDate: string;
  }) => {
    const url = `${backendBasePath}/admin/companies/${companyId}/activate_company_services`;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ service_code: serviceCode, activate_date: activateDate }),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanySupportCities = async (
    userType: UserType,
    company_id: string,
  ): Promise<{ data: { support_cities: SupportCitiesData[] }; status: 'success' | 'error' }> => {
    const url = `${backendBasePath}/${userType}/companies/${company_id}/support_cities`;
    let status: 'error' | 'success' = 'error';
    const data = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, data };
  };

  const registerSupportCities = async (
    userType: UserType,
    company_id: string,
    payload: {
      city_codes: number[];
    },
  ) => {
    const url = `${backendBasePath}/${userType}/companies/${company_id}/support_cities`;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ city_codes: payload.city_codes }),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateDisclosureStatementCompanyBuyer = async (
    company_id: string,
    disclosure_statement_company_buyer: keyof typeof DisclosureStatementCompanyBuyerOptions,
  ) => {
    const url = `${backendBasePath}/admin/companies/${company_id}/company_operational_detail`;
    let status;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ disclosure_statement_company_buyer }),
    })
      .then((response) => {
        if (response.status === 200) {
          status = 'success';
          return response.json();
        } else {
          status = 'error';
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return { status, result };
  };

  const forcePublish = async (companyId: string) => {
    const url = `${backendBasePath}/admin/companies/${companyId}/complete_forced_registration`;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  /**
   * 会社の検索軸別のアピール文一覧を取得する
   * @param company_id 会社のid
   * @returns
   */
  const getCompanyTopTitles = async (
    company_id: string,
  ): Promise<{ status: 'success' | 'error'; data: CompanyTopTitles }> => {
    const url = `${backendBasePath}/admin/companies/${company_id}/company_top_titles`;
    let status: statusType = 'error';
    const data = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, data };
  };

  /**
   * 会社の検索軸別のアピール文一覧を取得する
   * @param company_id 会社のid
   * @returns
   */
  const updateCompanyTopTitles = async (
    company_id: string,
    payload: { top_titles: CompanyTopTitles['company_top_titles'] },
  ): Promise<{ success: boolean }> => {
    const url = `${backendBasePath}/admin/companies/${company_id}/company_top_titles`;
    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  return {
    getCompaniesListByAdmin,
    getCompaniesListByCompany,
    getPublicCompaniesList,
    getCompanyDetail,
    updateCompanyInformationByAdmin,
    getCompanyOverView,
    updateCompanyOverView,
    getCompanyProfileData,
    getRejectedCompanyProfileData,
    getCompanyLastApprovedProfileData,
    createCompany,
    deleteCompany,
    updateStep,
    updateCompanyDetail,
    updateCompanyInformation,
    updateCompanyTags,
    updateCompanyProfile,
    updateCompanyProfileRequest,
    updateCompanyProfileRequests,
    getCompanyUserData,
    getCompanyUserDetail,
    getCompanyUsersAddress,
    createCompanyUser,
    editCompanyUser,
    deleteCompanyUser,
    getCompanyNotificationEmails,
    createCompanyNotificationEmails,
    deleteCompanyNotificationEmails,
    getCompanyOptions,
    getCompanyData,
    createProfileImages,
    selectMainProfileImage,
    deleteProfileImage,
    getUnapprovedProfileImages,
    classifyProfileImage,
    getCompanyTags,
    activateCompanyService,
    updateDisclosureStatementCompanyBuyer,
    getCompanySupportCities,
    registerSupportCities,
    forcePublish,
    getCompanyTopTitles,
    updateCompanyTopTitles,
  };
};
